import dayjs from "dayjs";
import * as XLSX from "xlsx";

export const UPLOAD_FILE_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,application/pdf,image/png,image/jpeg,image/webp,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const UPLOAD_FILE_EXTENSIONS = [
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ".xlsx",
  ".xls",
];

export function toUploadFieldValue(value) {
  if (value === null || value === undefined) return "";
  return String(value);
}

export function normalizeUploadDate(value) {
  const raw = toUploadFieldValue(value).trim();
  if (!raw) return "";
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
  if (slash) {
    const month = slash[1].padStart(2, "0");
    const day = slash[2].padStart(2, "0");
    const year = slash[3].length === 2 ? `20${slash[3]}` : slash[3];
    return `${year}-${month}-${day}`;
  }
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

export function cleanUploadNumber(value) {
  return toUploadFieldValue(value).replace(/,/g, "").trim();
}

export function uploadFileKind(name) {
  const ext = `.${String(name).split(".").pop().toLowerCase()}`;
  if (ext === ".pdf") return "pdf";
  if ([".png", ".jpg", ".jpeg", ".webp"].includes(ext)) return "image";
  if ([".xlsx", ".xls"].includes(ext)) return "excel";
  return "unknown";
}

/** Normalize excel preview to [{ sheetIndex, sheetName, grid }]. Supports legacy flat grids. */
export function normalizeExcelPreviewSheets(preview) {
  if (!Array.isArray(preview) || preview.length === 0) return [];
  const first = preview[0];
  if (first && typeof first === "object" && !Array.isArray(first) && "grid" in first) {
    return preview.map((sheet, index) => ({
      sheetIndex:
        Number.isFinite(Number(sheet.sheetIndex)) && Number(sheet.sheetIndex) > 0
          ? Number(sheet.sheetIndex)
          : index + 1,
      sheetName: String(sheet.sheetName || `Sheet${index + 1}`),
      grid: Array.isArray(sheet.grid) ? sheet.grid : [],
    }));
  }
  // Legacy: flat grid from the first sheet only.
  return [
    {
      sheetIndex: 1,
      sheetName: "Sheet1",
      grid: preview,
    },
  ];
}

export async function buildExcelPreview(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const names = workbook.SheetNames || [];
  if (!names.length) return [];
  return names.map((name, index) => {
    const sheet = workbook.Sheets[name];
    const grid = sheet
      ? XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: "",
        }).slice(0, 12)
      : [];
    return {
      sheetIndex: index + 1,
      sheetName: name,
      grid,
    };
  });
}

function extractWorkOrderFromExcelGrid(rows) {
  const grid = Array.isArray(rows) ? rows : [];
  for (const row of grid) {
    const cells = (Array.isArray(row) ? row : []).map((cell) =>
      String(cell ?? "").trim(),
    );
    for (let i = 0; i < cells.length; i += 1) {
      const cell = cells[i];
      const same = cell.match(
        /(?:work\s*order\s*#?|wo\s*#)\s*[:.]?\s*(TX\d{5,}|\d{6,10})\b/i,
      );
      if (same) return same[1];
      if (!/^\s*work\s*order\s*#?\s*$/i.test(cell) && !/^\s*wo\s*#\s*$/i.test(cell)) {
        continue;
      }
      for (let k = i + 1; k < Math.min(i + 3, cells.length); k += 1) {
        const value = cells[k].replace(/\.0$/, "");
        if (/^(TX\d{5,}|\d{6,10})$/i.test(value)) return value;
      }
    }
  }
  return "";
}

/** Read Work Order from the Excel preview. Multi-sheet skips override so extract WO wins. */
export function extractWorkOrderFromExcelPreview(preview) {
  const sheets = normalizeExcelPreviewSheets(preview);
  if (sheets.length !== 1) return "";
  return extractWorkOrderFromExcelGrid(sheets[0].grid);
}

export function mapFlushTypeToDeadEnd(flushType) {
  const value = toUploadFieldValue(flushType).trim().toLowerCase();
  if (value === "dead end" || value === "de" || value === "yes") return "Yes";
  if (
    value === "poor water quality" ||
    value === "pwq" ||
    value === "no" ||
    value === "other"
  ) {
    return "No";
  }
  return toUploadFieldValue(flushType);
}

export function mapDeviceTypeToFlushingDevice(deviceType) {
  const value = toUploadFieldValue(deviceType).trim().toLowerCase();
  if (value === "fv" || value === "fire value" || value === "fire valve") {
    return "FV";
  }
  if (value === "bo" || value === "blow off" || value === "blowoff") {
    return "BO";
  }
  if (value === "hy" || value === "hydrant") return "HY";
  return toUploadFieldValue(deviceType);
}

export function mapExtractItemToMaintenanceRow(item, index = 0) {
  const source = item || {};
  return {
    previewId: `upload-preview-${index}`,
    date: normalizeUploadDate(source.Date),
    workOrder: toUploadFieldValue(source.WorkOrder),
    volume: cleanUploadNumber(source.Gallons),
    location: toUploadFieldValue(source.Address),
    gpm: cleanUploadNumber(source.GPM),
    minutes: cleanUploadNumber(source.Minutes),
    category: toUploadFieldValue(source.Category) || "Maintenance",
    postFlushResidual: cleanUploadNumber(source.PostFlushResidual),
    deadEnd: mapFlushTypeToDeadEnd(source.FlushType),
    flushingDevice: mapDeviceTypeToFlushingDevice(source.DeviceType),
    pageNumber:
      source.PageNumber == null || source.PageNumber === ""
        ? null
        : Number.isFinite(Number(source.PageNumber))
          ? Number(source.PageNumber)
          : null,
    lowConfidence:
      source.LowConfidence === true ||
      String(source.LowConfidence).trim().toUpperCase() === "Y",
  };
}

export function mapImportTypeToCategory(type) {
  const value = toUploadFieldValue(type).trim();
  if (!value) return "Maintenance";
  const lowered = value.toLowerCase();
  if (lowered === "maintainance" || lowered === "maintenance") {
    return "Maintenance";
  }
  if (lowered === "leak") return "Leak";
  return value;
}

export function resolveImportSourceRowNumber(row) {
  if (!row || typeof row !== "object") return null;
  const explicit = row.sourceRowNumber ?? row.source_row_number;
  if (explicit == null || explicit === "") return null;
  const parsed = Number(explicit);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
}

export const EXTRACTED_SNAPSHOT_FIELDS = [
  "date",
  "workOrder",
  "volume",
  "location",
  "gpm",
  "minutes",
  "category",
  "postFlushResidual",
  "deadEnd",
];

export function normalizeEditableCompareValue(field, value) {
  if (field === "date") {
    return normalizeUploadDate(value);
  }
  if (
    field === "volume" ||
    field === "gpm" ||
    field === "minutes" ||
    field === "postFlushResidual"
  ) {
    const cleaned = cleanUploadNumber(value);
    if (cleaned === "") return "";
    const asNumber = Number(cleaned);
    if (Number.isFinite(asNumber)) {
      return String(asNumber);
    }
    return cleaned;
  }
  if (field === "deadEnd") {
    return mapFlushTypeToDeadEnd(value).trim();
  }
  if (field === "category") {
    return mapImportTypeToCategory(value).trim() || "Maintenance";
  }
  return toUploadFieldValue(value).trim();
}

export function getRowExtractedSnapshot(row) {
  const raw = row?.extractedSnapshot ?? row?.extracted_snapshot;
  if (!raw || typeof raw !== "object") return null;
  return raw;
}

export function getRowEditedFields(row) {
  const snapshot = getRowExtractedSnapshot(row);
  if (!snapshot) return [];
  const edited = [];
  for (const field of EXTRACTED_SNAPSHOT_FIELDS) {
    const current = normalizeEditableCompareValue(field, row?.[field]);
    const original = normalizeEditableCompareValue(field, snapshot[field]);
    if (current !== original) edited.push(field);
  }
  return edited;
}

export function isMaintenanceRowEdited(row) {
  return getRowEditedFields(row).length > 0;
}

export function fileHasExtractedSnapshots(group) {
  const rows = group?.rows || [];
  if (!rows.length) return false;
  return rows.every((row) => getRowExtractedSnapshot(row) != null);
}

export function fileHasEditedRows(group) {
  return (group?.rows || []).some((row) => isMaintenanceRowEdited(row));
}

export function mapImportRowToMaintenanceRow(row, fileIndex = 0) {
  const sourceRowNumber = resolveImportSourceRowNumber(row);
  if (sourceRowNumber == null) return null;
  const sourcePage = row.source_page ?? row.sourcePage ?? row.pageNumber;
  const snapshot = getRowExtractedSnapshot(row);
  return {
    previewId: `upload-preview-${fileIndex}-${sourceRowNumber - 1}`,
    sourceRowNumber,
    rowId: row.rowId ?? row.id ?? null,
    extractedSnapshot: snapshot,
    date: normalizeUploadDate(row.date),
    workOrder: toUploadFieldValue(row.work_order ?? row.workOrder),
    volume: cleanUploadNumber(row.flow_value ?? row.volume),
    location: toUploadFieldValue(row.location),
    gpm: cleanUploadNumber(row.flow_rate_value ?? row.gpm),
    minutes: cleanUploadNumber(row.flush_duration ?? row.minutes),
    category: mapImportTypeToCategory(row.type ?? row.category),
    postFlushResidual: cleanUploadNumber(
      row.post_flush_residual ?? row.postFlushResidual,
    ),
    deadEnd: mapFlushTypeToDeadEnd(row.flush_reason ?? row.deadEnd),
    flushingDevice: mapDeviceTypeToFlushingDevice(
      row.flushing_device ?? row.flushingDevice,
    ),
    pageNumber:
      sourcePage == null || sourcePage === ""
        ? null
        : Number.isFinite(Number(sourcePage))
          ? Number(sourcePage)
          : sourcePage,
    lowConfidence: false,
  };
}

export function mergeExtractedSnapshotsIntoGroups(groups, apiRows) {
  if (!Array.isArray(groups) || !groups.length) return groups;
  const byFile = new Map();
  for (const apiRow of Array.isArray(apiRows) ? apiRows : []) {
    const fileId = resumeFileId(apiRow?.fileId ?? apiRow?.file_id);
    const sourceRowNumber = resolveImportSourceRowNumber(apiRow);
    if (fileId == null || sourceRowNumber == null) continue;
    const key = `${fileId}:${sourceRowNumber}`;
    byFile.set(key, apiRow);
  }
  return groups.map((group, fileIndex) => {
    const fileId = resumeFileId(group?.fileId);
    if (fileId == null) return group;
    return {
      ...group,
      rows: (group.rows || []).map((row, rowIndex) => {
        const sourceRowNumber =
          resolveImportSourceRowNumber(row) ??
          resolveImportSourceRowNumber({
            sourceRowNumber: rowIndex + 1,
          });
        if (sourceRowNumber == null) return row;
        const apiRow = byFile.get(`${fileId}:${sourceRowNumber}`);
        if (!apiRow) return row;
        const mapped = mapImportRowToMaintenanceRow(apiRow, fileIndex);
        if (!mapped) {
          return {
            ...row,
            sourceRowNumber,
            rowId: apiRow.rowId ?? apiRow.id ?? row.rowId ?? null,
            extractedSnapshot: getRowExtractedSnapshot(apiRow),
          };
        }
        return {
          ...row,
          ...mapped,
          previewId: row.previewId || mapped.previewId,
          lowConfidence: Boolean(row.lowConfidence),
        };
      }),
    };
  });
}

export function applyResetApiRowToUiRow(uiRow, apiRow, fileIndex = 0) {
  const mapped = mapImportRowToMaintenanceRow(
    {
      ...apiRow,
      sourceRowNumber:
        apiRow?.sourceRowNumber ??
        apiRow?.source_row_number ??
        uiRow?.sourceRowNumber,
    },
    fileIndex,
  );
  if (!mapped) return uiRow;
  return {
    ...uiRow,
    ...mapped,
    previewId: uiRow.previewId || mapped.previewId,
    lowConfidence: Boolean(uiRow.lowConfidence),
  };
}

function resumeFileId(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
}

export function buildResumeReviewGroups({ job, files, rows } = {}) {
  const fileList = Array.isArray(files) ? files : [];
  if (!fileList.length) {
    return { error: "no-files" };
  }
  const rowsByFile = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const fileId = resumeFileId(row?.fileId ?? row?.file_id);
    if (fileId == null) {
      return { error: "missing-file-id" };
    }
    const list = rowsByFile.get(fileId) || [];
    list.push(row);
    rowsByFile.set(fileId, list);
  }

  const groups = [];
  for (let index = 0; index < fileList.length; index += 1) {
    const file = fileList[index];
    const fileId = resumeFileId(file?.fileId ?? file?.id);
    if (fileId == null) {
      return { error: "missing-file-id" };
    }
    const mappedRows = [];
    for (const row of rowsByFile.get(fileId) || []) {
      const mapped = mapImportRowToMaintenanceRow(row, index);
      if (!mapped) {
        return { error: "missing-source-row" };
      }
      mappedRows.push(mapped);
    }
    mappedRows.sort((left, right) => left.sourceRowNumber - right.sourceRowNumber);
    const status = file?.status;
    const previewUrl = file?.previewUrl || file?.preview_url || "";
    groups.push({
      localId: `resume-${job?.id ?? "job"}-${fileId}`,
      fileName: file?.fileName || file?.file_name || "upload",
      fileType: uploadFileKind(file?.fileName || file?.file_name || ""),
      previewUrl,
      excelPreview: [],
      previewUnavailable: !previewUrl,
      rows: mappedRows,
      error:
        status === "failed"
          ? toUploadFieldValue(file?.errorMessage || file?.error_message)
          : "",
      warning: "",
      verified: status === "verified",
      page: 1,
      fileId,
    });
  }
  return {
    groups,
    jobId: job?.id ?? job?.jobId ?? null,
  };
}
